import React from 'react';
import { Message, User } from '../types';

interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
  currentUser: User;
  onReply: (message: Message) => void;
  onDelete: (messageId: string) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isOwn,
  currentUser,
  onReply,
  onDelete
}) => {
  const messageTime = new Date(message.timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const canDelete = isOwn || currentUser.username === 'nepcoder';

  if (isOwn) {
    return (
      <div className="flex justify-end mb-2 message-bubble">
        <div className="max-w-xs lg:max-w-md">
          {message.replyTo && (
            <div className="bg-white/20 border-l-2 border-white/40 pl-3 py-2 mb-2 rounded-r-lg">
              <div className="text-xs text-white/80 font-medium">{message.replyToUsername}</div>
              <div className="text-sm text-white/90 truncate">{message.replyToMessage || 'File'}</div>
            </div>
          )}
          <div className="bg-gradient-to-r from-telegram-blue to-blue-600 text-white rounded-2xl rounded-br-md px-4 py-3 shadow-lg relative group">
            {message.type === 'message' ? (
              <div className="text-sm leading-relaxed break-words">{message.message}</div>
            ) : (
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="bi bi-file-earmark text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{message.fileName}</div>
                  <div className="text-xs opacity-75">{formatFileSize(message.fileSize || 0)}</div>
                </div>
                <button 
                  onClick={() => window.open(message.fileUrl, '_blank')}
                  className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                >
                  <i className="bi bi-download"></i>
                </button>
              </div>
            )}
            <div className="flex items-center justify-end space-x-1 mt-1">
              <span className="text-xs opacity-75">{messageTime}</span>
              <i className="bi bi-check2-all text-xs opacity-75"></i>
            </div>
            
            {/* Message Actions */}
            <div className="absolute -left-16 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
              <button 
                onClick={() => onReply(message)}
                className="p-2 bg-gray-800/80 text-white rounded-full hover:bg-gray-700 transition-colors" 
                title="Reply"
              >
                <i className="bi bi-reply text-sm"></i>
              </button>
              <button 
                onClick={() => onDelete(message.id)}
                className="p-2 bg-red-600/80 text-white rounded-full hover:bg-red-500 transition-colors" 
                title="Delete"
              >
                <i className="bi bi-trash text-sm"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start space-x-3 mb-2 message-bubble">
      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-gray-400 to-gray-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
        {message.profilePicture ? (
          <img src={message.profilePicture} alt={message.username} className="w-full h-full object-cover" />
        ) : (
          <span className="text-white text-sm font-medium">{message.username.charAt(0).toUpperCase()}</span>
        )}
      </div>
      <div className="max-w-xs lg:max-w-md">
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium">{message.username}</div>
        {message.replyTo && (
          <div className="bg-gray-100 dark:bg-gray-700 border-l-2 border-telegram-blue pl-3 py-2 mb-2 rounded-r-lg">
            <div className="text-xs text-telegram-blue font-medium">{message.replyToUsername}</div>
            <div className="text-sm text-gray-600 dark:text-gray-300 truncate">{message.replyToMessage || 'File'}</div>
          </div>
        )}
        <div className="bg-white dark:bg-gray-800 rounded-2xl rounded-bl-md px-4 py-3 shadow-lg relative group">
          {message.type === 'message' ? (
            <div className="text-sm leading-relaxed break-words text-gray-800 dark:text-white">{message.message}</div>
          ) : (
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <i className="bi bi-file-earmark text-lg text-gray-600 dark:text-gray-300"></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate text-gray-800 dark:text-white">{message.fileName}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(message.fileSize || 0)}</div>
              </div>
              <button 
                onClick={() => window.open(message.fileUrl, '_blank')}
                className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <i className="bi bi-download text-gray-600 dark:text-gray-300"></i>
              </button>
            </div>
          )}
          <div className="flex items-center justify-end mt-1">
            <span className="text-xs text-gray-500 dark:text-gray-400">{messageTime}</span>
          </div>
          
          {/* Message Actions */}
          <div className="absolute -right-16 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
            <button 
              onClick={() => onReply(message)}
              className="p-2 bg-gray-800/80 text-white rounded-full hover:bg-gray-700 transition-colors" 
              title="Reply"
            >
              <i className="bi bi-reply text-sm"></i>
            </button>
            {canDelete && (
              <button 
                onClick={() => onDelete(message.id)}
                className="p-2 bg-red-600/80 text-white rounded-full hover:bg-red-500 transition-colors" 
                title="Delete"
              >
                <i className="bi bi-trash text-sm"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;